<template>
  <md-dialog
    ref="printerModal"
    :md-click-outside-to-close="false"
    :md-close-on-esc="false"
    class="printer-modal"
  >
    <md-dialog-title>
      <span>Configurações de Impressão</span>
      <md-button
        class="md-icon-button close-button"
        @click="close"
      >
        <md-icon>close</md-icon>
      </md-button>
    </md-dialog-title>

    <md-dialog-content class="printer-modal-content">
      <div
        v-if="loading"
        class="loading-center"
      >
        <div class="loading-content">
          <loading />
          <p class="loading-text">
            Carregando impressoras...
          </p>
        </div>
      </div>

      <div
        v-else-if="calibrating"
        class="loading-center"
      >
        <div class="loading-content">
          <loading />
          <p class="loading-text">
            Calibrando impressora...
          </p>
        </div>
      </div>

      <div v-else>
        <!-- Configuração de Impressora -->
        <div
          v-if="!loadingFailed"
          class="config-section"
        >
          <h3 class="section-title">
            Definição da Impressora
          </h3>
          <label class="section-label">Selecione sua Impressora</label>
          <div class="select-container">
            <multiselect
              v-model="selectedPrinter"
              :options="formattedPrinters"
              placeholder="Selecione uma impressora"
              label="label"
              track-by="value"
              :searchable="true"
              :show-labels="false"
              :close-on-select="true"
              :allow-empty="true"
            />
          </div>
        </div>

        <!-- Calibração -->
        <div class="calibration-section">
          <h3 class="section-title">
            Calibração da Impressora:
          </h3>
          <label class="section-label">Selecione o modelo da sua impressora:</label>
          <div class="flex flex-row gap-3">
            <div class="select-container">
              <multiselect
                v-model="selectedModel"
                :options="modelOptions"
                placeholder="Selecione um modelo"
                label="label"
                track-by="value"
                :searchable="true"
                :show-labels="false"
                :close-on-select="true"
                :allow-empty="true"
                :disabled="!selectedPrinter"
              />
            </div>
            <button
              class="btn printer-calibration-button"
              :disabled="!selectedPrinter || !selectedModel || calibrating"
              @click="calibratePrinter"
            >
              <md-tooltip>Calibrar impressora</md-tooltip>
              <md-icon md-src="/static/images/icon-printer-calibration.svg" />
            </button>
          </div>

          <div
            v-if="!selectedPrinter"
            class="warning-message"
          >
            <md-icon class="warning-icon">
              warning
            </md-icon>
            <span>Selecione uma impressora antes de escolher o modelo</span>
          </div>
        </div>
      </div>
    </md-dialog-content>

    <!-- Alert de erro -->
    <div
      v-if="calibrationError"
      class="error-alert"
    >
      <div class="error-alert-content">
        <md-icon class="error-alert-icon">
          error
        </md-icon>
        <span class="error-alert-text">{{ calibrationError }}</span>
        <md-button
          class="md-icon-button error-alert-close"
          @click="clearError"
        >
          <md-icon>close</md-icon>
        </md-button>
      </div>
    </div>

    <md-dialog-actions class="modal-actions">
      <md-button
        class="md-button cancel-button"
        @click="close"
      >
        CANCELAR
      </md-button>
      <md-button
        class="md-button btn btn-primary md-theme-default px-8"
        @click="close"
      >
        Salvar
      </md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { usePrinter } from "./composables/usePrinter";
import notify from "@/api/notification";
import printerService from "@/api/printer";
import PrintWebSocketService from "@/services/PrintWebSocketService";
import Multiselect from "vue-multiselect";
import Loading from "@/components/common/Loading";

export default {
  name: "PrinterModal",
  components: {
    Multiselect,
    Loading,
  },
  data() {
    return {
      selectedPrinter: null,
      selectedModel: null,
      loading: false,
      loadingFailed: false,
      calibrating: false,
      calibrationError: null,
      modelOptions: [
        { value: "TLP2844", label: "ZEBRA - TLP2844" },
        { value: "GC420", label: "ZEBRA - GC420 / ZD220" },
      ],
      printerHelpers: usePrinter(),
    };
  },
  computed: {
    ...mapGetters(["availablePrinters", "defaultPrinter", "webSocketStatus"]),
    formattedPrinters() {
      return this.printerHelpers.formatPrinters(this.availablePrinters);
    },
  },
  watch: {
    selectedPrinter(newValue, oldValue) {
      if (newValue) {
        const printerValue = newValue?.value || newValue;
        const defaultPrinterValue = this.defaultPrinter?.value || this.defaultPrinter;

        if (printerValue !== defaultPrinterValue) {
          this.saveSelectedPrinter(printerValue);
        }
      } else {
        this.selectedModel = null;
      }
    },
  },
  methods: {
    async open() {
      this.loadingFailed = false;
      this.$refs.printerModal.open();
      this.resetForm();

      this.loading = true;
      try {
        await this.printerHelpers.loadPrinters(this.$store);

        if (this.defaultPrinter) {
          const defaultPrinterObj = this.formattedPrinters.find((p) => p.value === this.defaultPrinter);
          this.selectedPrinter = defaultPrinterObj || this.defaultPrinter;
        } else {
          this.selectedPrinter = null;
        }
      } catch (error) {
        this.loadingFailed = true;
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$refs.printerModal.close();
      this.resetForm();
    },
    resetForm() {
      if (!this.defaultPrinter) {
        this.selectedPrinter = null;
      }
      this.selectedModel = null;
      this.loadingFailed = false;
      this.calibrating = false;
      this.calibrationError = null;
    },
    saveSelectedPrinter(printerValue) {
      this.printerHelpers.setDefaultPrinter(printerValue, this.$store);

      const selectedPrinter = this.formattedPrinters.find((p) => p.value === printerValue);

      notify({
        title: "SUCESSO:",
        message: `Impressora "${selectedPrinter?.label || printerValue}" selecionada como padrão!`,
      });
    },
    async calibratePrinter() {
      if (!this.validateCalibrationRequirements()) {
        return;
      }

      this.startCalibration();

      try {
        const modelValue = this.getModelValue();
        let calibrationResult = null;

        if (this.isWebSocketConnected()) {
          const labelResponse = await this.getPrinterModelLabel(modelValue);
          const configLabel = this.extractConfigLabel(labelResponse, modelValue);
          calibrationResult = await this.calibrateViaWebSocket(configLabel, modelValue);
        } else {
          calibrationResult = await this.calibrateViaAPI(modelValue);
        }

        this.showSuccessNotification(calibrationResult);
        this.trackCalibrationEvent(modelValue);
      } catch (error) {
        this.handleCalibrationError(error);
      } finally {
        this.finishCalibration();
      }
    },

    validateCalibrationRequirements() {
      if (!this.selectedPrinter) {
        this.calibrationError = "Selecione uma impressora antes de calibrar.";
        return false;
      }
      
      if (!this.selectedModel) {
        this.calibrationError = "Selecione um modelo de impressora para calibrar.";
        return false;
      }
      return true;
    },

    startCalibration() {
      this.calibrating = true;
      this.calibrationError = null;
    },

    finishCalibration() {
      this.calibrating = false;
    },

    getModelValue() {
      return this.selectedModel?.value || this.selectedModel;
    },

    getPrinterValue() {
      return this.selectedPrinter?.value || this.selectedPrinter;
    },

    isWebSocketConnected() {
      return this.webSocketStatus.connectionState === "connected";
    },

    async calibrateViaWebSocket(configLabel = null, modelValue = null) {
      const printerValue = this.getPrinterValue();
      return await PrintWebSocketService.calibratePrinter(printerValue, configLabel, modelValue);
    },

    async calibrateViaAPI(modelValue) {
      return await printerService.calibratePrinter(modelValue);
    },

    async getPrinterModelLabel(modelValue) {
      return await printerService.getPrinterModels();
    },

    extractConfigLabel(apiResponse, modelValue) {
      const modelsData = apiResponse?.data;
      if (modelsData && typeof modelsData === 'object') {
        return modelsData[modelValue] || null;
      }
      return null;
    },

    showSuccessNotification(calibrationResult = null) {
      const isWebSocket = this.isWebSocketConnected();
      let message;

      if (isWebSocket && calibrationResult) {
        message = calibrationResult.message || "Calibração da impressora concluída com sucesso!";
      } else if (isWebSocket) {
        message = "Calibração da impressora iniciada via WebSocket…";
      } else {
        message = "Calibração da impressora iniciada via API…";
      }

      notify({
        title: "SUCESSO:",
        message: message,
      });
    },

    trackCalibrationEvent(modelValue) {
      this.$analytics.pushAnalyticsEvent("ga-event", {
        category: "Calibrar Impressora",
        action: "Calibragem de impressora modelo - " + modelValue,
      });
    },

    handleCalibrationError(error) {
      let errorMessage = "Erro ao calibrar impressora. Tente novamente.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      this.calibrationError = errorMessage;
    },
    clearError() {
      this.calibrationError = null;
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss" scoped>
@import "../../../../static/stylesheets/variables";

.printer-modal {
  :deep(.md-dialog) {
    min-width: 500px;
    max-width: 600px;
    overflow: visible;
  }

  :deep(.md-dialog-title) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0;
    font-size: 20px;
    font-weight: 500;
    color: #333;

    .close-button {
      margin: 0;
      min-width: auto;
      width: 32px;
      height: 32px;

      .md-icon {
        font-size: 20px;
        color: #666;
      }
    }
  }
}

.printer-modal-content {
  padding: 24px;
  min-height: 300px;
  overflow: visible;
}

.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 250px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  color: #666;
  margin-bottom: 0;
}

.error-alert {
  padding: 0 24px;
  margin-bottom: 16px;

  .error-alert-content {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 4px;
    color: #dc2626;

    .error-alert-icon {
      margin-right: 12px;
      color: #dc2626;
      font-size: 18px;
    }

    .error-alert-text {
      flex: 1;
      font-size: 14px;
      line-height: 1.4;
    }

    .error-alert-close {
      margin: 0;
      min-width: auto;
      width: 24px;
      height: 24px;

      .md-icon {
        font-size: 16px;
        color: #dc2626;
      }
    }
  }
}

.config-section,
.calibration-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 500;
    color: #7f1a54;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #7f1a54;
  }

  .section-label {
    display: block;
    font-size: 14px;
    font-weight: 400;
    color: #666;
    margin-bottom: 8px;
  }

  .select-container {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 2px;
    flex: 1;
    &:hover {
      border-color: #bbb;
    }

    &:focus-within {
      border-color: #7f1a54;
    }

    :deep(.multiselect) {
      min-height: 40px;
      border: none;
      border-radius: 0;

      .multiselect__tags {
        border: none;
        border-radius: 0;
        padding: 8px 12px;
        background: transparent;
        min-height: 40px;
        font-size: 14px;
      }

      .multiselect__single {
        padding: 0;
        background: transparent;
        color: #333;
        font-size: 14px;
        margin-bottom: 0;
      }

      .multiselect__placeholder {
        color: #999;
        font-size: 14px;
        margin-bottom: 0;
        padding: 0;
      }

      .multiselect__input {
        border: none;
        font-size: 14px;
        padding: 0;
        margin-bottom: 0;
        background: transparent;
      }

      .multiselect__select {
        height: 40px;
        padding: 0 12px;

        &:before {
          color: #999;
          border-color: #999 transparent transparent;
        }
      }

      .multiselect__content-wrapper {
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-top: 1px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      .multiselect__option {
        padding: 12px 16px;
        font-size: 14px;
        color: #333;

        &:hover,
        &.multiselect__option--highlight {
          background: #f5f5f5;
          color: #333;
        }

        &.multiselect__option--selected {
          background: #7f1a54;
          color: white;
          font-weight: 500;
        }
      }
    }
  }

  .warning-message {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #fff8e1;
    border: 1px solid #ffcc02;
    border-radius: 4px;
    color: #f57c00;
    font-size: 14px;

    .warning-icon {
      margin: 0;
      margin-right: 8px;
      font-size: 18px;
      color: #f57c00;
    }
  }
}

.modal-actions {
  padding: 16px 24px 24px;
  justify-content: space-between;

  .cancel-button {
    color: #666;
    font-weight: 500;
    text-transform: uppercase;
  }
}

.printer-calibration-button {
  width: 44px;
  height: 44px;
  background-color: $brand-primary;

  .md-icon {
    padding: 4px;
    width: 45px;
    height: 45px;
  }

  &:disabled {
    background-color: #ccc !important;
    cursor: not-allowed;
    
    .md-icon {
      opacity: 0.6;
    }
  }
}
</style>
